import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Layout, message, Select, Modal } from 'antd';
import { get } from 'lodash';
import BrandApi from 'services/brand';
import PdButton from 'components/pd-button';
import { PageSpinner } from 'components/page-spinner';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './style.scss';

const { Option } = Select;
const { Content } = Layout;
const { confirm } = Modal;

export function BrandList() {
  const [brandsActive, setBrandsActive] = useState([]);
  const [brandsInactive, setBrandsInactive] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [limit] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [category, setCategory] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  async function getBrandListActive(category) {
    const { data } = await BrandApi.getBrandList(category, 'active');
    const brandList = data.data;
    setBrandsActive(brandList);
  }

  async function getBrandListInactive(category) {
    const { data } = await BrandApi.getBrandList(category, 'inactive');
    const brandList = data.data;
    setBrandsInactive(brandList);
  }

  async function getBrandList(category) {
    try {
      setLoadingPage(true);
      await getBrandListActive(category);
      await getBrandListInactive(category);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingPage(false);
    }
  }

  async function getCategoryList() {
    try {
      const { data } = await BrandApi.getCategoryList();
      const cate = data.data;
      setCategory(cate);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    }
  }

  async function hideBrand(id) {
    try {
      if (id) {
        await BrandApi.hideBrand(id);
        message.success('successfully hide brand');
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getBrandList();
    }
  }

  async function deleteBrand(id) {
    try {
      if (id) {
        await BrandApi.deleteBrand(id);
        message.success('Successfully removed brand');
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getBrandList();
    }
  }

  function handleHide(id) {
    confirm({
      title: 'Are you sure you want to hide this brand?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        hideBrand(id);
      },
      onCancel() {},
    });
  }

  function handleDelete(id) {
    confirm({
      title: 'Are you sure you want to delete?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteBrand(id);
      },
      onCancel() {},
    });
  }

  async function handleRestore(id) {
    try {
      if (id) {
        await BrandApi.restoreBrand(id);
        message.success('Successfully restored brand');
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getBrandList();
    }
  }

  useEffect(() => {
    getBrandList();
    getCategoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  function SortableItem({ value: brand, index }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: brand.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginTop: 20,
      userSelect: 'none',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="pd-cms-brand--list draggable-item">
        <Row type="flex" align="middle" gutter={30}>
          <Col span={1}>
            <div {...listeners} style={{ cursor: 'grab', padding: '0 10px' }}>
              ⋮⋮
            </div>
          </Col>
          <Col span={4} {...listeners}>
            <div className="pd-cms-brand--picture">
              <img className="image" src={brand.image_url} alt="logo brand" />
            </div>
          </Col>
          <Col span={5} {...listeners}>
            <div className="pd-cms-brand--text">
              <div className="table-title--sort">{brand.title}</div>
            </div>
          </Col>
          <Col span={7} className="pd-cms-brand--cate" {...listeners}>
            {brand.categories.map((cate, i) => (
              <div key={i} className="categories">
                {cate.title}
              </div>
            ))}
          </Col>
          <Col span={2} {...listeners}>
            <div className="status-indicator">
              <span
                className={brand.status !== 'inactive' ? 'active' : 'hidden'}>
                {displayStatus(brand.status)}
              </span>
            </div>
          </Col>
          <Col span={5}>
            <div className="button-table">
              <Row>
                <Col span={12} className="button">
                  {brand.status === 'active' ? (
                    <PdButton
                      type="danger"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHide(brand.id);
                      }}>
                      Deactivate
                    </PdButton>
                  ) : (
                    <PdButton
                      type="primary"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(brand.id);
                      }}>
                      Reactivate
                    </PdButton>
                  )}
                </Col>
                <Col span={12} className="button">
                  {brand.status === 'active' ? (
                    <Link
                      to={{
                        pathname: "/brands/edit", search: `id=${brand.id}`,
                      }}>
                      <PdButton type="primary" full style={{ height: 32 }}>
                        Edit
                      </PdButton>
                    </Link>
                  ) : (
                    <PdButton
                      type="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(brand.id);
                      }}
                      full
                      style={{ height: 32 }}>
                      Delete
                    </PdButton>
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  function SortableList({ items, onSortEnd }) {
    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = items.findIndex((item, index) => (item.id || index) === active.id);
        const newIndex = items.findIndex((item, index) => (item.id || index) === over.id);
        onSortEnd({ oldIndex, newIndex });
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item, index) => ({ id: item.id || index }))} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem key={item.id || index} value={item} index={index} />
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  async function onSortEndActive({ oldIndex, newIndex }) {
    let list = [...brandsActive];
    let temp = list[oldIndex];
    list.splice(oldIndex, 1);
    list.splice(newIndex, 0, temp);

    let payload = [];
    list.forEach((item, index) => {
      let order_attr = {
        id: item.id,
        order_level: index,
      };
      payload.push(order_attr);
    });

    try {
      setBrandsActive(list);
      await BrandApi.updateBrandOrder(payload);
      message.success('Successfully updated brand order');
    } catch {
      message.error('Cannot connect to server, please check connection');
    }
  }

  return (
    <Layout className="pd-cms-brand main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Brands</div>
          <Content className="pageContent">
            <Row gutter={20}>
              <Col span={24}>
                <Row gutter={30} className="no-margin">
                  <Col span={17}>
                    <span>Category : </span>
                    <Select
                      style={{ width: 150 }}
                      defaultValue={categoryFilter}
                      placeholder="Select Category"
                      onChange={(val) => {
                        val === 'all' ? getBrandList() : getBrandList(val);
                        setCategoryFilter(val);
                      }}>
                      <Option value="all">All</Option>
                      {category.map((cate, i) => (
                        <Option key={i} value={cate.id}>
                          {cate.title}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={7} className=" float-right">
                    <Link to={'/brands/create'}>
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32, width: 176 }}>
                        Add New Brand
                      </PdButton>
                    </Link>
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <div id="drag-content-active">
                  <SortableList
                    items={brandsActive}
                    onSortEnd={onSortEndActive}
                  />
                </div>
              </Col>

              {brandsInactive.length > 0 && (
                <Col span={24} className="hidden-title-container">
                  <div className="pageTitle hiddenTitle">Inactive Brands</div>
                </Col>
              )}

              <Col span={24}>
                <div id="drag-content-inactive">
                  <SortableList items={brandsInactive} />
                </div>
              </Col>
            </Row>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default { BrandList };
